import React from 'react';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import {formatDistance, formatElevation} from '../../utils/formatters';
import useGetSyncPromise, {GetSyncPromise, SyncPromise} from '../../utils/react/useGetSyncPromise';
import TrackStatsCalculator from '../../utils/gaia/TrackStatsCalculator';
import getSyncTrackMultiLine from '../../utils/gaia/getSyncTrackMultiLine';
import fetchElevation from '../../utils/gaia/fetchElevation';
import fetchUser from '../../utils/gaia/fetchUser';

interface TrackStatsProps extends React.HTMLProps<HTMLDivElement> {
    track: Track;
    project: MapProject;
    interaction: MapInteraction;
}

export default function TrackStats({track, project, interaction, ...props}: TrackStatsProps) {
    const getSyncPromise = useGetSyncPromise();
    const [user] = getSyncPromise(fetchUser());

    const [stats, error] = getSyncTrackStats(track, project, getSyncPromise, interaction);

    if (error) {
        return <div {...props}>
            <Typography color="inherit" variant="body1" style={{fontSize: 12}}>
                Error loading elevation
            </Typography>
        </div>;

    } else if (stats && track.isGeometryLoaded) {
        return <div {...props}>
            <Typography color="inherit" variant="body1" style={{fontSize: 12}}>
                <strong>Distance:</strong> {formatDistance(stats.distance, user)}
            </Typography>
            <Typography color="inherit" variant="body1" style={{fontSize: 12}}>
                +{formatElevation(stats.ascent, user)} / -{formatElevation(stats.descent, user)}
            </Typography>
        </div>;
    } else {
        return <div {...props} style={{height: 36, ...props.style}}>
            <CircularProgress disableShrink thickness={7} style={{height: 36, width: 36}} />
        </div>;
    }
}

interface TrackStats {
    average_speed: number;
    distance: number;
    moving_speed: number;
    moving_time: number;
    stopped_time: number;
    ascent: number;
    descent: number;
    total_time: number;
}

function getSyncTrackStats(
    track: Track,
    project: MapProject,
    getSyncPromise: GetSyncPromise,
    interaction: MapInteraction,
): SyncPromise<TrackStats | undefined> {

    if (interaction.subtype === 'draggingPoint') return [undefined, undefined, false];

    const [feature,, isLoaded] = getSyncTrackMultiLine({
        track,
        project,
        interaction,
        getSyncPromise,
    });
    if (!isLoaded || !feature) return [undefined, undefined, false];

    const [geometry] = getSyncPromise(fetchElevation(feature.geometry));
    if (!geometry) return [undefined, undefined, false];

    const calculator: any = new TrackStatsCalculator();
    calculator.addGeoJson(geometry);
    calculator.filter();
    return [calculator.getJsonStats(), undefined, true];
}
